<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-70">
    <!-- Container Modal -->
    <div class="w-full max-w-3xl m-3 overflow-hidden transition-all duration-200 bg-gray-800 rounded-lg shadow-xl">
      <!-- Header -->
      <div class="flex items-center justify-between p-4 border-b border-gray-600">
        <div class="flex items-center gap-3">
          <!-- Item Image -->
          <img 
            :src="item.img || 'default-item.png'" 
            alt="Item Image" 
            class="object-cover w-12 h-12 rounded-md border-2 sm:h-20 sm:w-20" 
            :class="item.rarityColors?.borderColor || 'border-indigo-400'" 
            @error="handleImageError"
          />
          <!-- Name & Type & Star Rating -->
          <div>
            <h1 class="text-lg font-semibold" :class="item.rarityColors?.textColor || 'text-indigo-200'">
              {{ item.name }}
            </h1>
            <p class="text-xs text-indigo-200 sm:text-sm">ประเภท: {{ item.type }} {{ item.subtype ? `(${item.subtype})` : '' }}</p>
            <div v-if="item.rarity" class="flex items-center gap-1 mt-1">
              <template v-for="star in Number(item.rarity)" :key="star">
                <i class="text-xs fas fa-star text-yellow-400 sm:text-sm"></i>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="p-4">
        <div class="flex border-b border-gray-600 overflow-x-auto">
          <button
            @click="activeTab = 'info'"
            class="px-4 py-2 text-sm font-medium transition-colors duration-200 whitespace-nowrap"
            :class="activeTab === 'info' ? 'text-indigo-200 border-b-2 border-indigo-500' : 'text-indigo-200 hover:bg-gray-700 hover:text-indigo-300'"
            :aria-selected="activeTab === 'info'"
            aria-label="ข้อมูลไอเทม"
          >
            ข้อมูลไอเทม
          </button>
          <button
            @click="activeTab = 'dropLocations'"
            class="px-4 py-2 text-sm font-medium transition-colors duration-200 whitespace-nowrap"
            :class="activeTab === 'dropLocations' ? 'text-indigo-200 border-b-2 border-indigo-500' : 'text-indigo-200 hover:bg-gray-700 hover:text-indigo-300'"
            :aria-selected="activeTab === 'dropLocations'"
            aria-label="สถานที่ดรอป"
          >
            สถานที่ดรอป
          </button>
          <button
            v-if="item.decomposable"
            @click="activeTab = 'decompose'"
            class="px-4 py-2 text-sm font-medium transition-colors duration-200 whitespace-nowrap"
            :class="activeTab === 'decompose' ? 'text-indigo-200 border-b-2 border-indigo-500' : 'text-indigo-200 hover:bg-gray-700 hover:text-indigo-300'"
            :aria-selected="activeTab === 'decompose'"
            aria-label="แยกชิ้นส่วน"
          >
            แยกชิ้นส่วน
          </button>
          <button
            v-if="item.upgradable"
            @click="activeTab = 'upgrade'"
            class="px-4 py-2 text-sm font-medium transition-colors duration-200 whitespace-nowrap"
            :class="activeTab === 'upgrade' ? 'text-indigo-200 border-b-2 border-indigo-500' : 'text-indigo-200 hover:bg-gray-700 hover:text-indigo-300'"
            :aria-selected="activeTab === 'upgrade'"
            aria-label="อัพเกรด"
          >
            อัพเกรด
          </button>
        </div>
      </div>

      <!-- Content -->
      <div class="px-4 py-4 space-y-4 overflow-y-auto max-h-96">
        <!-- Error Message -->
        <div v-if="error" class="mb-4 text-sm text-center text-red-300">
          {{ error }}
        </div>

        <!-- Tab: Item Info -->
        <div v-if="activeTab === 'info'">
          <!-- Basic Information Section -->
          <section class="bg-gray-700 p-4 rounded-md">
            <h2 class="text-sm font-semibold text-indigo-200 mb-2">ข้อมูลพื้นฐาน</h2>
            <div class="grid grid-cols-2 gap-4 text-sm">
              <div v-if="item.type !== 'ทั่วไป'" class="flex items-center gap-2">
                <span class="text-indigo-200">เลเวลที่ใช้ได้: <strong>{{ item.requiredLevel }}</strong></span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-indigo-200">น้ำหนัก: <strong>{{ item.weight || 0 }}</strong> กก.</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-indigo-200">ราคา:</span>
                <i v-if="item.currencyType === 'gold'" class="text-yellow-400 fa-solid fa-g"></i>
                <i v-if="item.currencyType === 'diamond'" class="text-blue-400 fa-solid fa-gem"></i>
                <i v-else class="text-gray-100 fa-solid fa-coins"></i>
                <strong class="text-gray-100">{{ item.price.toLocaleString() }}</strong>
              </div>
            </div>
          </section>

          <!-- Description Section -->
          <section v-if="item.description" class="bg-gray-700 p-4 rounded-md">
            <h2 class="text-sm font-semibold text-indigo-200 mb-2">คำอธิบาย</h2>
            <p class="text-sm leading-relaxed text-indigo-200">
              {{ item.description }}
            </p>
          </section>

          <!-- Statistics Section -->
          <section v-if="hasStats" class="bg-gray-700 p-4 rounded-md">
            <div v-if="item.stats?.stamina >= 1 || item.stats?.str >= 1 || item.stats?.agi >= 1 || item.stats?.int >= 1 || item.stats?.luck >= 1">
              <h2 class="mb-2 text-sm font-semibold text-indigo-200">ค่าสถานะหลัก</h2>
              <div class="grid grid-cols-2 gap-4 text-sm">
                <div v-if="item.stats?.stamina" class="flex items-center gap-2">
                  <span class="text-indigo-200">Stamina: <strong>{{ item.stats.stamina }}</strong></span>
                </div>
                <div v-if="item.stats?.str" class="flex items-center gap-2">
                  <span class="text-indigo-200">Str: <strong>{{ item.stats.str }}</strong></span>
                </div>
                <div v-if="item.stats?.agi" class="flex items-center gap-2">
                  <span class="text-indigo-200">Agi: <strong>{{ item.stats.agi }}</strong></span>
                </div>
                <div v-if="item.stats?.int" class="flex items-center gap-2">
                  <span class="text-indigo-200">Int: <strong>{{ item.stats.int }}</strong></span>
                </div>
                <div v-if="item.stats?.luck" class="flex items-center gap-2">
                  <span class="text-indigo-200">Luck: <strong>{{ item.stats.luck }}</strong></span>
                </div>
              </div>
            </div>
            <div v-if="item.stats?.maxHp >= 1 || item.stats?.atk >= 1 || item.stats?.evasion >= 1 || item.stats?.critical >= 1 || item.stats?.defense >= 1" class="mt-4">
              <h2 class="mb-2 text-sm font-semibold text-indigo-200">ค่าสถานะเสริม</h2>
              <div class="grid grid-cols-2 gap-4 text-sm">
                <div v-if="item.stats?.maxHp" class="flex items-center gap-2">
                  <span class="text-indigo-200">พลังชีวิต: <strong>{{ item.stats.maxHp }}</strong></span>
                </div>
                <div v-if="item.stats?.atk" class="flex items-center gap-2">
                  <span class="text-indigo-200">พลังโจมตี: <strong>{{ item.stats.atk }}</strong></span>
                </div>
                <div v-if="item.stats?.evasion" class="flex items-center gap-2">
                  <span class="text-indigo-200">หลบหลีก: <strong>{{ item.stats.evasion }}</strong></span>
                </div>
                <div v-if="item.stats?.critical" class="flex items-center gap-2">
                  <span class="text-indigo-200">คริติคอล: <strong>{{ item.stats.critical }}</strong></span>
                </div>
                <div v-if="item.stats?.defense" class="flex items-center gap-2">
                  <span class="text-indigo-200">พลังป้องกัน: <strong>{{ item.stats.defense }}</strong></span>
                </div>
              </div>
            </div>
          </section>

          <!-- Food-Specific Info -->
          <section v-if="item.type === 'อาหาร'" class="bg-gray-700 p-4 rounded-md">
            <h2 class="mb-2 text-sm font-semibold text-indigo-200">ข้อมูลอาหาร</h2>
            <div class="grid grid-cols-2 gap-4 text-sm">
              <div class="flex items-center gap-2">
                <span class="text-indigo-200">ระดับอาหาร: <strong>{{ item.foodLevel || 0 }}</strong></span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-indigo-200">ระดับความสุข: <strong>{{ item.happinessLevel || 0 }}</strong></span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-indigo-200">ระดับพลังงาน: <strong>{{ item.energyLevel || 0 }}</strong></span>
              </div>
            </div>
          </section>

          <!-- Healing-Specific Info -->
          <section v-if="item.type === 'รักษา'" class="bg-gray-700 p-4 rounded-md">
            <h2 class="mb-2 text-sm font-semibold text-indigo-200">ข้อมูลการรักษา</h2>
            <div class="grid grid-cols-2 gap-4 text-sm">
              <div class="flex items-center gap-2">
                <span class="text-indigo-200">HP ที่ได้รับ: <strong>{{ item.healHpPercentage || 0 }}%</strong></span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-indigo-200">การรักษาติดเชื้อไวรัส: <strong>{{ item.virusHealPercentage || 0 }}%</strong></span>
              </div>
            </div>
          </section>

          <!-- Trap-Specific Info -->
          <section v-if="item.type === 'กัปดัก'" class="bg-gray-700 p-4 rounded-md">
            <h2 class="mb-2 text-sm font-semibold text-indigo-200">ข้อมูลกัปดัก</h2>
            <div class="grid grid-cols-2 gap-4 text-sm">
              <div v-if="item.diceCount" class="flex items-center gap-2">
                <span class="text-indigo-200">จำนวนลูกเต๋า: <strong>{{ item.diceCount }}</strong></span>
              </div>
              <div v-if="item.damage" class="flex items-center gap-2">
                <span class="text-indigo-200">สร้างความเสียหาย: <strong>{{ item.damage }}</strong></span>
              </div>
              <div v-if="item.durationHours" class="flex items-center gap-2">
                <span class="text-indigo-200">ระยะเวลาชั่วโมง: <strong>{{ item.durationHours }}</strong></span>
              </div>
              <div v-if="item.useCount" class="flex items-center gap-2">
                <span class="text-indigo-200">จำนวนครั้งที่ใช้งานได้: <strong>{{ item.useCount }}</strong></span>
              </div>
            </div>
            <div v-if="item.disarmTrapItems && item.disarmTrapItems.length > 0" class="mt-4">
              <h3 class="mb-2 text-sm font-semibold text-indigo-200">ไอเทมปลดกัปดักที่สามารถทำลายได้</h3>
              <div class="flex flex-wrap gap-2">
                <span v-for="disarmItem in item.disarmTrapItems" :key="disarmItem" class="px-2 py-1 text-sm text-indigo-200 bg-gray-600 rounded-md">
                  {{ disarmItem }}
                </span>
              </div>
            </div>
          </section>

          <!-- Bomb-Specific Info -->
          <section v-if="item.type === 'ระเบิด'" class="bg-gray-700 p-4 rounded-md">
            <h2 class="mb-2 text-sm font-semibold text-indigo-200">ข้อมูลระเบิด</h2>
            <div class="grid grid-cols-2 gap-4 text-sm">
              <div v-if="item.damage" class="flex items-center gap-2">
                <span class="text-indigo-200">ความเสียหาย: <strong>{{ item.damage }}</strong></span>
              </div>
            </div>
          </section>

          <!-- Vehicle-Specific Info -->
          <section v-if="item.type === 'พาหนะ'" class="bg-gray-700 p-4 rounded-md">
            <h2 class="mb-2 text-sm font-semibold text-indigo-200">ข้อมูลพาหนะ</h2>
            <div class="grid grid-cols-2 gap-4 text-sm">
              <div v-if="item.fuelLevel" class="flex items-center gap-2">
                <span class="text-indigo-200">ระดับน้ำมัน: <strong>{{ item.fuelLevel }}</strong></span>
              </div>
            </div>
          </section>

          <!-- Disarm Trap-Specific Info -->
          <section v-if="item.type === 'ปลดกัปดัก'" class="bg-gray-700 p-4 rounded-md">
            <h2 class="mb-2 text-sm font-semibold text-indigo-200">ข้อมูลปลดกัปดัก</h2>
            <div class="grid grid-cols-2 gap-4 text-sm">
              <div v-if="item.disarmRoomCount" class="flex items-center gap-2">
                <span class="text-indigo-200">จำนวนห้องที่สามารถปลดกัปดักได้: <strong>{{ item.disarmRoomCount }}</strong></span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-indigo-200">แจ้งเตือนเมื่อปลดกัปดัก: <strong>{{ item.notify ? 'ใช่' : 'ไม่' }}</strong></span>
              </div>
            </div>
            <div v-if="item.nonDestroyableTraps && item.nonDestroyableTraps.length > 0" class="mt-4">
              <h3 class="mb-2 text-sm font-semibold text-indigo-200">กัปดักที่ไม่สามารถทำลายได้</h3>
              <div class="flex flex-wrap gap-2">
                <span v-for="trap in item.nonDestroyableTraps" :key="trap" class="px-2 py-1 text-sm text-indigo-200 bg-gray-600 rounded-md">
                  {{ trap }}
                </span>
              </div>
            </div>
          </section>

          <!-- Loot-Specific Info -->
          <section v-if="item.type === 'ปล้น'" class="bg-gray-700 p-4 rounded-md">
            <h2 class="mb-2 text-sm font-semibold text-indigo-200">ข้อมูลการปล้น</h2>
            <div class="grid grid-cols-2 gap-4 text-sm">
              <div class="flex items-center gap-2">
                <span class="text-indigo-200">ระยะเวลาที่ต้องรอ: <strong>{{ item.waitTimeMinutes || 0 }}</strong> นาที</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-indigo-200">จำนวนปล้นไอเทมในกระเป๋าขั้นต่ำ: <strong>{{ item.minLootItems || 0 }}</strong></span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-indigo-200">จำนวนปล้นไอเทมในกระเป๋าขั้นสูงสุด: <strong>{{ item.maxLootItems || 0 }}</strong></span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-indigo-200">จำนวนเหรียญที่ได้รับ: <strong>{{ item.silverReward || 0 }}</strong></span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-indigo-200">จำนวนทองที่ได้รับ: <strong>{{ item.goldReward || 0 }}</strong></span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-indigo-200">จำนวนเพชรที่ได้รับ: <strong>{{ item.diamondReward || 0 }}</strong></span>
              </div>
            </div>
          </section>

          <!-- Killer Robot-Specific Info -->
          <section v-if="item.type === 'หุ่นยนต์สังหาร'" class="bg-gray-700 p-4 rounded-md">
            <h2 class="mb-2 text-sm font-semibold text-indigo-200">ข้อมูลหุ่นยนต์สังหาร</h2>
            <div class="grid grid-cols-2 gap-4 text-sm">
              <div class="flex items-center gap-2">
                <span class="text-indigo-200">จำนวนครั้งที่อัปเกรดได้: <strong>{{ item.maxUpgradeCount || 0 }}</strong></span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-indigo-200">น้ำหนักที่รับได้: <strong>{{ item.maxLoad || 0 }}</strong> กก.</span>
              </div>
            </div>
            <div v-if="item.upgradeMaterials && item.upgradeMaterials.length > 0" class="mt-4">
              <h3 class="mb-2 text-sm font-semibold text-indigo-200">ไอเทมที่ใช้ในการอัปเกรด</h3>
              <div class="space-y-2">
                <div v-for="(material, index) in item.upgradeMaterials" :key="index" class="flex items-center gap-2 text-indigo-200">
                  <span>{{ material.itemName }}: <strong>{{ material.quantity }}</strong></span>
                </div>
              </div>
            </div>
            <div v-if="item.explorableMaps && item.explorableMaps.length > 0" class="mt-4">
              <h3 class="mb-2 text-sm font-semibold text-indigo-200">สถานที่ที่สามารถสำรวจได้</h3>
              <div class="space-y-2">
                <div v-for="(map, index) in item.explorableMaps" :key="index" class="flex items-center gap-2 text-indigo-200">
                  <span>{{ map }}</span>
                </div>
              </div>
            </div>
          </section>

          <!-- Cybernetic Organ-Specific Info -->
          <section v-if="item.type === 'อวัยวะจักรกล'" class="bg-gray-700 p-4 rounded-md">
            <h2 class="mb-2 text-sm font-semibold text-indigo-200">ข้อมูลอวัยวะจักรกล</h2>
            <div v-if="item.upgradeableStats && item.upgradeableStats.length > 0" class="mt-4">
              <h3 class="mb-2 text-sm font-semibold text-indigo-200">ค่าสถานะที่สามารถอัปเกรดได้</h3>
              <div class="flex flex-wrap gap-2">
                <span v-for="stat in item.upgradeableStats" :key="stat" class="px-2 py-1 text-sm text-indigo-200 bg-gray-600 rounded-md">
                  {{ stat.charAt(0).toUpperCase() + stat.slice(1) }}
                </span>
              </div>
            </div>
          </section>

          <!-- Card-Specific Info -->
          <section v-if="item.type === 'การ์ด'" class="bg-gray-700 p-4 rounded-md">
            <h2 class="mb-2 text-sm font-semibold text-indigo-200">ข้อมูลการ์ด</h2>
            <div class="grid grid-cols-2 gap-4 text-sm">
              <div class="flex items-center gap-2">
                <span class="text-indigo-200">อุปกรณ์ที่สามารถติดตั้งการ์ดนี้ได้: <strong>{{ item.compatibleEquipment || 'ไม่ระบุ' }}</strong></span>
              </div>
            </div>
          </section>

          <!-- Spirit-Specific Info -->
          <section v-if="item.type === 'วิญญาณ'" class="bg-gray-700 p-4 rounded-md">
            <h2 class="mb-2 text-sm font-semibold text-indigo-200">ข้อมูลวิญญาณ</h2>
            <div class="grid grid-cols-2 gap-4 text-sm">
              <div v-if="item.subtype === 'วิญญาณเฝ้าห้อง' && item.guardDurationHours" class="flex items-center gap-2">
                <span class="text-indigo-200">ระยะเวลาที่เฝ้า: <strong>{{ item.guardDurationHours }}</strong> ชั่วโมง</span>
              </div>
              <div v-if="item.subtype === 'วิญญาณเฝ้าห้อง' && item.fromMap" class="flex items-center gap-2">
                <span class="text-indigo-200">วิญญาณจากแผนที่: <strong>{{ item.fromMap }}</strong></span>
              </div>
              <div v-if="item.subtype === 'วิญญาณเฝ้าห้อง' && item.fromMap" class="flex items-center gap-2">
                <span class="text-indigo-200">จำนวนวิญญาณขั้นต่ำ: <strong>{{ item.minSpirits }}</strong></span>
              </div>
              <div v-if="item.subtype === 'วิญญาณเฝ้าห้อง' && item.maxSpirits" class="flex items-center gap-2">
                <span class="text-indigo-200">จำนวนวิญญาณสูงสุด: <strong>{{ item.maxSpirits }}</strong></span>
              </div>
              <div v-if="['วิญญาณเฝ้าห้อง', 'วิญญาณผู้พิทักษ์', 'วิญญาณดูดทรัพย์'].includes(item.subtype) && item.cooldownHours" class="flex items-center gap-2">
                <span class="text-indigo-200">ระยะเวลาคูดาวน์การใช้ครั้งถัดไป: <strong>{{ item.cooldownHours }}</strong> ชั่วโมง</span>
              </div>
              <div v-if="item.subtype === 'วิญญาณค้นหาผู้เล่น' && item.eliminatedRooms" class="flex items-center gap-2">
                <span class="text-indigo-200">จำนวนห้องที่ถูกคัดออก: <strong>{{ item.eliminatedRooms }}</strong></span>
              </div>
              <div v-if="item.subtype === 'วิญญาณผู้พิทักษ์' && item.protectCount" class="flex items-center gap-2">
                <span class="text-indigo-200">จำนวนเกราะที่สามารถป้องกันได้: <strong>{{ item.protectCount }}</strong></span>
              </div>
              <div v-if="item.subtype === 'วิญญาณผู้พิทักษ์' && item.protectDurationHours" class="flex items-center gap-2">
                <span class="text-indigo-200">ระยะเวลาคุ้มกัน: <strong>{{ item.protectDurationHours }}</strong> ชั่วโมง</span>
              </div>
              <div v-if="item.subtype === 'วิญญาณดูดทรัพย์' && item.minDiamondLoot" class="flex items-center gap-2">
                <span class="text-indigo-200">จำนวนปล้นเพชรขั้นต่ำ: <strong>{{ item.minDiamondLoot }}</strong></span>
              </div>
              <div v-if="item.subtype === 'วิญญาณดูดทรัพย์' && item.maxDiamondLoot" class="flex items-center gap-2">
                <span class="text-indigo-200">จำนวนปล้นเพชรขั้นสูงสุด: <strong>{{ item.maxDiamondLoot }}</strong></span>
              </div>
            </div>
            <!-- Required Previous Spirits Section -->
            <div v-if="item.requiredPreviousSpirits && item.requiredPreviousSpirits.length > 0" class="mt-4">
              <h3 class="mb-2 text-sm font-semibold text-indigo-200">วิญญาณที่ต้องเคยอัญเชิญมาก่อน</h3>
              <div class="flex flex-wrap gap-2">
                <span v-for="spirit in item.requiredPreviousSpirits" :key="spirit" class="px-2 py-1 text-sm text-indigo-200 bg-gray-600 rounded-md">
                  {{ spirit }}
                </span>
              </div>
            </div>
            <div v-else-if="item.type === 'วิญญาณ'" class="mt-4 text-sm text-center text-indigo-200">
              ไม่มีวิญญาณที่ต้องเคยอัญเชิญมาก่อน
            </div>
          </section>
        </div>

        <!-- Tab: Drop Locations -->
        <div v-if="activeTab === 'dropLocations'">
          <section>
            <div v-if="loading" class="flex items-center justify-center py-4">
              <div class="w-6 h-6 border-t-2 rounded-full animate-spin border-indigo-600"></div>
            </div>
            <div v-else-if="dropLocations.length === 0" class="text-sm text-center text-indigo-200">
              ไม่มีข้อมูลสถานที่ดรอปสำหรับไอเทมนี้
            </div>
            <div v-else class="space-y-3 overflow-y-auto max-h-80" id="style-1">
              <div v-for="map in dropLocations" :key="map.id" class="p-4 rounded-md bg-gray-700">
                <div class="flex items-center gap-3">
                  <img :src="map.imageUrl" alt="Map Image" class="object-cover w-12 h-12 rounded-md" @error="handleImageError" />
                  <div>
                    <h3 class="text-sm font-semibold text-indigo-200">{{ map.name }}</h3>
                  </div>
                </div>
                <div class="mt-3 space-y-2 text-sm text-indigo-200">
                  <div v-for="(drop, index) in map.drops" :key="index">
                    <p v-if="drop.type === 'main'">
                      โอกาสดรอปจากการสำเร็จแผนที่: <strong>{{ (drop.dropChance / 100).toFixed(2) }}%</strong>
                    </p>
                    <p v-else>
                      โอกาสดรอปจากสถานการณ์ "{{ drop.situationName }}": <strong>{{ (drop.dropChance / 100).toFixed(2) }}%</strong> (โอกาสเกิดสถานการณ์: {{ drop.situationChance }}%)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <!-- Tab: Decompose -->
        <div v-if="activeTab === 'decompose'">
          <section>
            <div v-if="decomposeLoading" class="flex items-center justify-center py-4">
              <div class="w-6 h-6 border-t-2 rounded-full animate-spin border-indigo-600"></div>
            </div>
            <div v-else-if="decomposeItems.length === 0" class="text-sm text-center text-indigo-200">
              ไม่มีข้อมูลการแยกชิ้นส่วนสำหรับไอเทมนี้
            </div>
            <div v-else class="space-y-3 overflow-y-auto max-h-80" id="style-1">
              <div v-for="decomposeItem in decomposeItems" :key="decomposeItem.itemName" class="flex items-center gap-3 p-4 rounded-md bg-gray-700">
                <img 
                  :src="decomposeItem.img || 'default-item.png'" 
                  alt="Decompose Item Image" 
                  class="object-cover w-12 h-12 border rounded-md" 
                  :class="decomposeItem.rarityColors?.borderColor || 'border-indigo-400'" 
                  @error="handleImageError"
                />
                <div>
                  <h3 class="text-sm font-semibold text-indigo-200">{{ decomposeItem.name }}</h3>
                  <p class="text-sm text-indigo-200">โอกาส: <strong>{{ decomposeItem.chance.toFixed(2) }}%</strong></p>
                </div>
              </div>
            </div>
          </section>
        </div>

        <!-- Tab: Upgrade Preview -->
        <div v-if="activeTab === 'upgrade'">
          <section>
            <h2 class="mb-3 text-sm font-semibold text-indigo-200">ตัวอย่างการอัพเกรด</h2>
            <div v-if="item.upgradable && item.upgradeTo" class="space-y-3 overflow-y-auto max-h-80" id="style-1">
              <div class="p-4 rounded-md bg-gray-700">
                <h3 class="text-sm font-semibold text-indigo-200">อัพเกรดไปยัง: {{ item.upgradeTo }}</h3>
                <p class="text-sm text-indigo-200">โอกาสสำเร็จ: <strong>{{ item.upgradeChance.toFixed(2) }}%</strong></p>
              </div>
            </div>
            <div v-else-if="sortedUpgradeList.length > 0" class="space-y-3 overflow-y-auto max-h-80" id="style-1">
              <div v-for="upgrade in sortedUpgradeList" :key="upgrade.lv" class="p-4 rounded-md bg-gray-700">
                <div class="mb-2">
                  <h3 class="text-sm font-semibold text-indigo-200">เลเวล {{ upgrade.lv }}</h3>
                </div>
                <div class="grid grid-cols-2 gap-4 mb-2 text-sm text-indigo-200">
                  <p>โอกาสสำเร็จ: <strong>{{ upgrade.chance.toFixed(2) }}%</strong></p>
                  <p>ราคาอัพเกรด: <strong>{{ upgrade.upgradePrice.toLocaleString() }}</strong> เหรียญ</p>
                </div>
                <div class="grid grid-cols-2 gap-4 text-sm text-indigo-200">
                  <div v-if="upgrade.stats?.stamina > 0">
                    <span>Stamina: <strong>{{ upgrade.stats.stamina }}</strong></span>
                  </div>
                  <div v-if="upgrade.stats?.str > 0">
                    <span>Str: <strong>{{ upgrade.stats.str }}</strong></span>
                  </div>
                  <div v-if="upgrade.stats?.agi > 0">
                    <span>Agi: <strong>{{ upgrade.stats.agi }}</strong></span>
                  </div>
                  <div v-if="upgrade.stats?.int > 0">
                    <span>Int: <strong>{{ upgrade.stats.int }}</strong></span>
                  </div>
                  <div v-if="upgrade.stats?.luck > 0">
                    <span>Luck: <strong>{{ upgrade.stats.luck }}</strong></span>
                  </div>
                  <div v-if="upgrade.stats?.maxHp > 0">
                    <span>Max HP: <strong>{{ upgrade.stats.maxHp }}</strong></span>
                  </div>
                  <div v-if="upgrade.stats?.atk > 0">
                    <span>Atk: <strong>{{ upgrade.stats.atk }}</strong></span>
                  </div>
                  <div v-if="upgrade.stats?.evasion > 0">
                    <span>Evasion: <strong>{{ upgrade.stats.evasion }}</strong></span>
                  </div>
                  <div v-if="upgrade.stats?.critical > 0">
                    <span>Critical: <strong>{{ upgrade.stats.critical }}</strong></span>
                  </div>
                  <div v-if="upgrade.stats?.defense > 0">
                    <span>Defense: <strong>{{ upgrade.stats.defense }}</strong></span>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-sm text-center text-indigo-200">
              ไม่มีข้อมูลการอัพเกรดสำหรับไอเทมนี้
            </div>
          </section>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex justify-end p-4 border-t border-gray-600">
        <button 
          @click="closeModal" 
          class="px-4 py-2 text-sm font-medium text-indigo-200 transition-colors duration-200 bg-indigo-600 rounded-md hover:bg-indigo-700"
          aria-label="ปิด"
        >
          ปิด
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import { ref, computed, onMounted } from "vue";
import { projectFirestore } from "@/firebase/config";
import gameItemsData from "@/data/gameItems.json";

export default {
  name: "ItemModal",
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  emits: ["close"],
  setup(props, { emit }) {
    const activeTab = ref("info");
    const dropLocations = ref([]);
    const loading = ref(false);
    const decomposeItems = ref([]);
    const decomposeLoading = ref(false);
    const error = ref('');

    // Handle image load errors
    const handleImageError = (event) => {
      event.target.src = event.target.alt.includes("Map") ? 'default-map.png' : 'default-item.png';
    };

    // Fetch drop locations from maps (including situations)
    const fetchDropLocations = async () => {
      loading.value = true;
      error.value = '';
      try {
        const snapshot = await projectFirestore.collection("maps").get();
        const locations = [];
        snapshot.forEach(doc => {
          const mapData = doc.data();
          const drops = [];

          // Check main rewards
          const mainReward = mapData.rewards?.find(r => r.itemName === props.item.name);
          if (mainReward) {
            drops.push({ type: 'main', dropChance: mainReward.dropChance || 0 });
          }

          // Check situations
          if (mapData.situations) {
            mapData.situations.forEach(situation => {
              const sitReward = situation.rewards?.find(r => r.itemName === props.item.name);
              if (sitReward) {
                drops.push({
                  type: 'situation',
                  situationName: situation.name,
                  situationChance: situation.chance || 0,
                  dropChance: sitReward.dropChance || 0
                });
              }
            });
          }

          // Add map if it has any drops
          if (drops.length > 0) {
            locations.push({
              id: doc.id,
              name: mapData.name,
              imageUrl: mapData.imageUrl || "default-map.png",
              requiredLevel: mapData.requiredLevel || 1,
              drops: drops
            });
          }
        });
        dropLocations.value = locations.sort((a, b) => a.requiredLevel - b.requiredLevel);
      } catch (error) {
        console.error("Error fetching drop locations:", error);
        error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลสถานที่ดรอป';
        dropLocations.value = [];
      } finally {
        loading.value = false;
      }
    };

    // Fetch decompose items
    const fetchDecomposeItems = async () => {
      if (!props.item.decomposable || !props.item.decomposeList || props.item.decomposeList.length === 0) {
        decomposeItems.value = [];
        return;
      }

      decomposeLoading.value = true;
      error.value = '';
      try {
        const items = props.item.decomposeList.map((decompose) => {
          if (!decompose.itemName) return null;
          const data = gameItemsData[decompose.itemName];
          return {
            itemName: decompose.itemName,
            name: data?.name || decompose.itemName,
            img: data?.img || 'default-item.png',
            chance: decompose.chance || 0,
            rarityColors: data?.rarityColors || { borderColor: 'border-indigo-400' },
          };
        }).filter(item => item !== null);
        decomposeItems.value = items.sort((a, b) => b.chance - a.chance);
      } catch (error) {
        console.error('Error fetching decompose items:', error);
        error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลการแยกชิ้นส่วน';
        decomposeItems.value = [];
      } finally {
        decomposeLoading.value = false;
      }
    };

    // Check if item has stats
    const hasStats = computed(() => {
      if (props.item.type === 'ปลดกัปดัก' || props.item.type === 'ปล้น') return false;
      const s = props.item.stats || {};
      return (
        s.stamina ||
        s.str ||
        s.agi ||
        s.int ||
        s.luck ||
        s.maxHp ||
        s.atk ||
        s.defense ||
        s.evasion ||
        s.critical
      );
    });

    // Sorted upgrade list
    const sortedUpgradeList = computed(() => {
      return [...(props.item.upgradeList || [])].sort((a, b) => a.lv - b.lv);
    });

    // Close modal
    const closeModal = () => {
      emit("close");
    };

    // Initialize data
    onMounted(() => {
      fetchDropLocations();
      fetchDecomposeItems();
    });

    return {
      activeTab,
      dropLocations,
      loading,
      decomposeItems,
      decomposeLoading,
      hasStats,
      sortedUpgradeList,
      closeModal,
      error,
      handleImageError,
    };
  },
};
</script>

<style scoped>
/* Custom Scrollbar */
#style-1::-webkit-scrollbar {
  width: 5px;
}

#style-1::-webkit-scrollbar-track {
  background: #374151;
  border-radius: 10px;
}

#style-1::-webkit-scrollbar-thumb {
  background: #4b5563;
  border-radius: 10px;
}

#style-1::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

/* Animations */
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

button:not(:disabled):active {
  transform: scale(0.98);
}
</style>